/**
 * Toast - Generic toast notification component
 * 
 * Features:
 * - Multiple types (info, success, warning, error, verification)
 * - Countdown timer support
 * - Multiple action buttons
 * - Auto-dismiss functionality
 * - Stacking support (via portal)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import logger from '../../utils/logger';
import './Toast.css';

/**
 * Format seconds to MM:SS
 */
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Toast Component
 * 
 * @param {string} id - Unique toast ID
 * @param {string} type - Toast type ('info', 'success', 'warning', 'error', 'verification')
 * @param {string} title - Toast title
 * @param {string} message - Toast message
 * @param {Array} actions - Array of action objects: {label, onClick, primary}
 * @param {number} countdown - Countdown duration in seconds (0 = no countdown)
 * @param {Function} onDismiss - Callback when toast is dismissed
 * @param {string} position - Toast position ('top-left', 'top-right', 'bottom-left', 'bottom-right')
 * @param {boolean} showCloseButton - Whether to show close button
 */
const Toast = ({
  id,
  type = 'info',
  title,
  message,
  actions = [],
  countdown = 0,
  onDismiss,
  position = 'top-left',
  showCloseButton = true,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(countdown);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  
  const timerRef = useRef(null);
  const timeoutRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Animate in on mount
  useEffect(() => {
    // Trigger animation
    animationFrameRef.current = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle countdown timer
  useEffect(() => {
    if (countdown > 0 && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            // Auto-dismiss when countdown reaches 0
            handleDismiss();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [countdown, timeRemaining]);

  // Handle dismiss with animation
  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    
    // Wait for exit animation to complete
    timeoutRef.current = setTimeout(() => {
      onDismiss?.();
    }, 300); // Match CSS animation duration
  }, [onDismiss]);

  // Handle action click
  // BUG-H3 FIX: Make async-aware to wait for onClick completion before dismissing
  // BUG #4 FIX: Prevent race condition from multiple rapid clicks
  const handleActionClick = useCallback(async (action) => {
    // Guard against concurrent clicks
    if (isActionLoading) return;
    
    setIsActionLoading(true);
    try {
      if (action.onClick) {
        await action.onClick();
      }
      handleDismiss();
    } catch (error) {
      logger.error('Toast action error:', error);
      // Toast will still dismiss on error to not trap user
      handleDismiss();
    } finally {
      setIsActionLoading(false);
    }
  }, [handleDismiss, isActionLoading]);

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'verification':
        return '🔐';
      case 'info':
      default:
        return 'ℹ️';
    }
  };

  const toastClasses = [
    'toast',
    `toast-${type}`,
    `toast-${position}`,
    isVisible ? 'toast-visible' : '',
    isExiting ? 'toast-exiting' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={toastClasses} data-toast-id={id}>
      <div className="toast-icon">
        {getIcon()}
      </div>
      
      <div className="toast-content">
        {title && <div className="toast-title">{title}</div>}
        {message && <div className="toast-message">{message}</div>}
        
        {actions.length > 0 && (
          <div className="toast-actions">
            {actions.map((action, index) => (
              <button
                key={index}
                className={`toast-action-btn ${action.primary ? 'toast-action-primary' : 'toast-action-secondary'}`}
                onClick={() => handleActionClick(action)}
                disabled={action.disabled || isActionLoading}
              >
                {action.label}
                {action.showCountdown && timeRemaining > 0 && ` (${formatTime(timeRemaining)})`}
              </button>
            ))}
          </div>
        )}
      </div>

      {showCloseButton && (
        <button 
          className="toast-close"
          onClick={handleDismiss}
          aria-label="Close"
        >
          ×
        </button>
      )}
    </div>
  );
};

Toast.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['info', 'success', 'warning', 'error', 'verification']),
  title: PropTypes.string,
  message: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    primary: PropTypes.bool,
    disabled: PropTypes.bool,
    showCountdown: PropTypes.bool,
  })),
  countdown: PropTypes.number,
  onDismiss: PropTypes.func,
  position: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
  showCloseButton: PropTypes.bool,
};

export default Toast;
